import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { MessageService } from "primeng/api";
import { TranslocoService } from "@ngneat/transloco";
import { Events } from "src/app/model/event";
import { OnlineUser } from "src/app/model/online-user";
import { AdminService } from "src/app/service/admin.service";
import { AccountService } from "src/app/service/account.service";
import { EventsService } from "src/app/service/events.service";
import { AuthService } from "src/app/service/auth.service";
import { enviroment } from "src/environments/environment";

@Component({
  selector: "app-admin",
  templateUrl: "./admin.component.html",
  styleUrl: "./admin.component.css",
})
export class AdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();


  isSuperAdmin = false;
  permissionsLoading = false;
  /** Waitlist HML: exibe o card de Controle de Acesso só onde a aprovação está ativa. */
  requireApproval = enviroment.REQUIRE_APPROVAL;

  // Online users
  onlineUsers: OnlineUser[] = [];
  isLoadingOnline = false;
  private onlineRefreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly ONLINE_REFRESH_MS = 30 * 1000;

  // Checkin mode
  isCheckinMode = false;
  isCheckUser = false;
  scannerActive = false;
  checkinCount = 0;
  selectedEventForCheckin: Events | null = null;
  eventConfirmed = false;
  lastScannedUser = "";
  isProcessingCheckin = false;
  events: Events[] = [];

  constructor(
    private adminService: AdminService,
    private accountService: AccountService,
    private eventsService: EventsService,
    private messageService: MessageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translocoService: TranslocoService,
    private titleService: Title,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.translocoService.selectTranslate("title.admin")
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.titleService.setTitle(value));

    this.resolvePermissions().then(() => {
      if (this.router.url.includes('management/checkin')) {
        this.isCheckinMode = true;
        this.getAllEvents();
      } else {
        this.loadOnlineUsers();
        this.onlineRefreshInterval = setInterval(() => this.loadOnlineUsers(), this.ONLINE_REFRESH_MS);
      }
    });
  }

  private async resolvePermissions(): Promise<void> {
    const cachedLevel = sessionStorage.getItem('_ui_pref_t');
    const cachedRole  = sessionStorage.getItem('_ui_role');

    if (cachedLevel === 'x2r' && cachedRole) {
      const role = this.authService.getAdminRole();
      this.isSuperAdmin = role !== 'support' && role !== 'operator';
      return;
    }

    // Permissões ainda não cacheadas — busca em background mostrando loading
    this.permissionsLoading = true;
    const userId = this.authService.getUserInformations();
    if (!userId) { this.router.navigate(['/login']); return; }

    try {
      const account = await this.accountService.getAccount(userId).toPromise();
      if (!account || account.accessLevel !== 2) {
        this.router.navigate(['/my-garage']);
        return;
      }
      const role = account.adminRole ?? 'super_admin';
      sessionStorage.setItem('_ui_pref_t', 'x2r');
      sessionStorage.setItem('_ui_role', role);
      this.isSuperAdmin = role !== 'support' && role !== 'operator';
    } catch {
      this.router.navigate(['/my-garage']);
    } finally {
      this.permissionsLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.onlineRefreshInterval !== null) {
      clearInterval(this.onlineRefreshInterval);
      this.onlineRefreshInterval = null;
    }
  }

  // ─── ONLINE USERS ───

  loadOnlineUsers(): void {
    this.isLoadingOnline = true;
    this.adminService.getOnlineUsers().subscribe({
      next: (data) => { this.onlineUsers = data; this.isLoadingOnline = false; },
      error: () => { this.isLoadingOnline = false; },
    });
  }

  refreshOnlineUsers(): void { this.loadOnlineUsers(); }

  // ─── CHECKIN MODE ───

  getAllEvents(): void {
    this.eventsService.getEvents().subscribe((data) => { this.events = data; });
  }

  startCheckinScanner(): void { this.scannerActive = true; }

  handleCheckinQrCode(qrCode: any): void {
    if (this.isProcessingCheckin) return;
    this.isProcessingCheckin = true;
    this.scannerActive = false;
    const userId = typeof qrCode === 'string' ? qrCode.trim() : qrCode.toString().trim();
    this.lastScannedUser = userId;

    if (!this.selectedEventForCheckin) {
      this.toast('error', this.translocoService.translate('common.labels.error'), this.translocoService.translate('adminPanel.messages.selectEventFirst'));
      this.isProcessingCheckin = false;
      return;
    }

    this.isCheckUser = true;
    this.eventsService.checkinEvent(userId, this.selectedEventForCheckin.id, this.selectedEventForCheckin.qrCode, true).subscribe({
      next: (response) => {
        this.isCheckUser = false;
        if (response.statusCode && response.statusCode !== 200) {
          this.toast('error', this.translocoService.translate('common.labels.error'), response.value || this.translocoService.translate('events.messages.checkInErrorDesc'));
        } else {
          this.toast('success', this.translocoService.translate('adminPanel.messages.checkInCompleted'), this.translocoService.translate('adminPanel.messages.userRegistered', { userId: userId.substring(0, 8) }));
          this.loadCheckinCount();
        }
        this.isProcessingCheckin = false;
      },
      error: () => {
        this.isCheckUser = false;
        this.isProcessingCheckin = false;
        this.toast('error', this.translocoService.translate('common.labels.error'), this.translocoService.translate('adminPanel.messages.checkInFailedVerify'));
      },
    });
  }

  onCheckinEventSelect(event: any): void { this.selectedEventForCheckin = event.value; }

  confirmEventSelection(): void {
    if (this.selectedEventForCheckin) { this.eventConfirmed = true; this.loadCheckinCount(); }
  }

  changeEvent(): void {
    this.eventConfirmed = false;
    this.selectedEventForCheckin = null;
    this.checkinCount = 0;
    this.lastScannedUser = "";
  }

  loadCheckinCount(): void {
    if (!this.selectedEventForCheckin) { this.checkinCount = 0; return; }
    this.eventsService.getQuantityCheckins(this.selectedEventForCheckin.id).subscribe({
      next: (checkins) => { this.checkinCount = checkins; },
      error: () => { this.checkinCount = 0; },
    });
  }

  private toast(severity: string, summary: string, detail: string): void {
    this.messageService.clear();
    this.messageService.add({ key: 'toastMessage', severity, summary, detail });
  }
}
